import React, { useState, useEffect } from 'react';
import aircraftTypeService, { AircraftType, CreateAircraftTypeRequest, UpdateAircraftTypeRequest } from '../services/aircraftTypeService';
import './AircraftTypeManagement.css';

interface AircraftTypeManagementProps {
  onClose?: () => void;
}

const AircraftTypeManagement: React.FC<AircraftTypeManagementProps> = ({ onClose }) => {
  const [aircraftTypes, setAircraftTypes] = useState<AircraftType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingAircraftType, setEditingAircraftType] = useState<AircraftType | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showActiveOnly, setShowActiveOnly] = useState(false);

  // Формы
  const [createForm, setCreateForm] = useState<CreateAircraftTypeRequest>({
    code: '',
    name: '',
    displayName: '',
    description: '',
    manufacturer: '',
    capacity: undefined
  });

  const [editForm, setEditForm] = useState<UpdateAircraftTypeRequest>({
    code: '',
    name: '',
    displayName: '',
    description: '',
    manufacturer: '',
    capacity: undefined
  });

  useEffect(() => {
    fetchAircraftTypes();
  }, []);

  const fetchAircraftTypes = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await aircraftTypeService.getAircraftTypes();
      setAircraftTypes(response.content);
    } catch (err) {
      console.error('Error fetching aircraft types:', err);
      setError('Ошибка при загрузке типов самолетов');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAircraftType = async () => {
    try {
      if (!createForm.code || !createForm.name || !createForm.displayName || !createForm.manufacturer) {
        alert('Пожалуйста, заполните все обязательные поля');
        return;
      }

      await aircraftTypeService.createAircraftType(createForm);
      setIsCreateModalOpen(false);
      setCreateForm({
        code: '',
        name: '',
        displayName: '',
        description: '',
        manufacturer: '',
        capacity: undefined
      });
      fetchAircraftTypes();
      alert('Тип самолета успешно создан!');
    } catch (err) {
      console.error('Error creating aircraft type:', err);
      alert('Ошибка при создании типа самолета');
    }
  };

  const handleUpdateAircraftType = async () => {
    try {
      if (!editingAircraftType) return;

      const updateData: UpdateAircraftTypeRequest = {};
      if (editForm.code) updateData.code = editForm.code;
      if (editForm.name) updateData.name = editForm.name;
      if (editForm.displayName) updateData.displayName = editForm.displayName;
      if (editForm.description !== undefined) updateData.description = editForm.description;
      if (editForm.manufacturer) updateData.manufacturer = editForm.manufacturer;
      if (editForm.capacity !== undefined) updateData.capacity = editForm.capacity;

      await aircraftTypeService.updateAircraftType(editingAircraftType.id, updateData);
      setEditingAircraftType(null);
      setEditForm({
        code: '',
        name: '',
        displayName: '',
        description: '',
        manufacturer: '',
        capacity: undefined
      });
      fetchAircraftTypes();
      alert('Тип самолета успешно обновлен!');
    } catch (err) {
      console.error('Error updating aircraft type:', err);
      alert('Ошибка при обновлении типа самолета');
    }
  };

  const handleDeleteAircraftType = async (aircraftType: AircraftType) => {
    if (!window.confirm(`Вы уверены, что хотите удалить тип самолета "${aircraftType.name}"?`)) {
      return;
    }

    try {
      await aircraftTypeService.deleteAircraftType(aircraftType.id);
      fetchAircraftTypes();
      alert('Тип самолета успешно удален!');
    } catch (err) {
      console.error('Error deleting aircraft type:', err);
      alert('Ошибка при удалении типа самолета');
    }
  };

  const handleCreateDefaultAircraftTypes = async () => {
    try {
      const defaultAircraftTypes = aircraftTypeService.getDefaultAircraftTypes();

      for (const aircraftTypeData of defaultAircraftTypes) {
        await aircraftTypeService.createAircraftType(aircraftTypeData);
      }

      fetchAircraftTypes();
      alert('Стандартные типы самолетов успешно созданы!');
    } catch (err) {
      console.error('Error creating default aircraft types:', err);
      alert('Ошибка при создании стандартных типов самолетов');
    }
  };

  const filteredAircraftTypes = aircraftTypes.filter(aircraftType => {
    const matchesSearch = 
      aircraftType.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      aircraftType.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      aircraftType.manufacturer.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesActive = !showActiveOnly || aircraftType.isActive;
    return matchesSearch && matchesActive;
  });

  const openEditModal = (aircraftType: AircraftType) => {
    setEditingAircraftType(aircraftType);
    setEditForm({
      code: aircraftType.code,
      name: aircraftType.name,
      displayName: aircraftType.displayName,
      description: aircraftType.description || '',
      manufacturer: aircraftType.manufacturer,
      capacity: aircraftType.capacity
    });
  };

  const closeEditModal = () => {
    setEditingAircraftType(null);
    setEditForm({
      code: '',
      name: '',
      displayName: '',
      description: '',
      manufacturer: '',
      capacity: undefined
    });
  };

  if (loading) {
    return (
      <div className="aircraft-type-management">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Загрузка типов самолетов...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="aircraft-type-management">
      <div className="aircraft-type-header">
        <h2>Управление типами самолетов</h2>
        <div className="aircraft-type-actions">
          <button 
            onClick={() => setIsCreateModalOpen(true)} 
            className="create-aircraft-type-btn"
          >
            Создать тип самолета
          </button>
          <button 
            onClick={handleCreateDefaultAircraftTypes} 
            className="create-default-btn"
          >
            Стандартные типы
          </button>
        </div>
      </div>

      {error && (
        <div className="error-container">
          <p className="error-message">{error}</p>
          <button onClick={fetchAircraftTypes} className="retry-button">
            Попробовать снова
          </button>
        </div>
      )}

      <div className="aircraft-type-filters">
        <input
          type="text"
          placeholder="Поиск по коду, названию или производителю..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        <label className="filter-checkbox">
          <input
            type="checkbox"
            checked={showActiveOnly}
            onChange={(e) => setShowActiveOnly(e.target.checked)}
          />
          Только активные
        </label>
      </div>

      <div className="aircraft-types-list">
        {filteredAircraftTypes.map((aircraftType) => (
          <div key={aircraftType.id} className={`aircraft-type-card ${!aircraftType.isActive ? 'inactive' : ''}`}>
            <div className="aircraft-type-info">
              <div className="aircraft-type-header-info">
                <div className="aircraft-type-code">{aircraftType.code}</div>
                <h3 className="aircraft-type-name">{aircraftType.name}</h3>
                <span className={`aircraft-type-active-badge ${aircraftType.isActive ? 'active' : 'inactive'}`}>
                  {aircraftType.isActive ? 'Активен' : 'Неактивен'}
                </span>
              </div>
              <div className="aircraft-type-details">
                <p><strong>Отображаемое название:</strong> {aircraftType.displayName}</p>
                <p><strong>Производитель:</strong> {aircraftType.manufacturer}</p>
                {aircraftType.capacity && (
                  <p><strong>Вместимость:</strong> {aircraftType.capacity} пассажиров</p>
                )}
                {aircraftType.description && (
                  <p><strong>Описание:</strong> {aircraftType.description}</p>
                )}
              </div>
              <div className="aircraft-type-meta">
                <span>Создан: {new Date(aircraftType.createdAt).toLocaleDateString('ru-RU')}</span>
                <span>Обновлен: {new Date(aircraftType.updatedAt).toLocaleDateString('ru-RU')}</span>
              </div>
            </div>
            <div className="aircraft-type-actions">
              <button 
                onClick={() => openEditModal(aircraftType)} 
                className="edit-btn"
              >
                Редактировать
              </button>
              <button 
                onClick={() => handleDeleteAircraftType(aircraftType)} 
                className="delete-btn"
              >
                Удалить
              </button>
            </div>
          </div>
        ))}
      </div>

      {filteredAircraftTypes.length === 0 && !loading && (
        <div className="empty-state">
          <p>Типы самолетов не найдены</p>
        </div>
      )}

      {/* Модальное окно создания */}
      {isCreateModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Создать новый тип самолета</h3>
              <button onClick={() => setIsCreateModalOpen(false)} className="close-btn">×</button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label>Код *</label>
                  <input
                    type="text"
                    value={createForm.code}
                    onChange={(e) => setCreateForm({ ...createForm, code: e.target.value })}
                    placeholder="B737"
                  />
                </div>
                <div className="form-group">
                  <label>Название *</label>
                  <input
                    type="text"
                    value={createForm.name}
                    onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                    placeholder="Boeing 737"
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Отображаемое название *</label>
                <input
                  type="text"
                  value={createForm.displayName}
                  onChange={(e) => setCreateForm({ ...createForm, displayName: e.target.value })}
                  placeholder="Boeing 737"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Производитель *</label>
                  <input
                    type="text"
                    value={createForm.manufacturer}
                    onChange={(e) => setCreateForm({ ...createForm, manufacturer: e.target.value })}
                    placeholder="Boeing"
                  />
                </div>
                <div className="form-group">
                  <label>Вместимость</label>
                  <input
                    type="number"
                    value={createForm.capacity || ''}
                    onChange={(e) => setCreateForm({ ...createForm, capacity: e.target.value ? parseInt(e.target.value) : undefined })}
                    placeholder="189"
                    min="1"
                    max="1000"
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Описание</label>
                <textarea
                  value={createForm.description}
                  onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
                  placeholder="Описание типа самолета"
                  rows={3}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={() => setIsCreateModalOpen(false)} className="cancel-btn">
                Отмена
              </button>
              <button onClick={handleCreateAircraftType} className="save-btn">
                Создать
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно редактирования */}
      {editingAircraftType && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Редактировать тип самолета</h3>
              <button onClick={closeEditModal} className="close-btn">×</button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label>Код</label>
                  <input
                    type="text"
                    value={editForm.code}
                    onChange={(e) => setEditForm({ ...editForm, code: e.target.value })}
                    placeholder="B737"
                  />
                </div>
                <div className="form-group">
                  <label>Название</label>
                  <input
                    type="text"
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    placeholder="Boeing 737"
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Отображаемое название</label>
                <input
                  type="text"
                  value={editForm.displayName}
                  onChange={(e) => setEditForm({ ...editForm, displayName: e.target.value })}
                  placeholder="Boeing 737"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Производитель</label>
                  <input
                    type="text"
                    value={editForm.manufacturer}
                    onChange={(e) => setEditForm({ ...editForm, manufacturer: e.target.value })}
                    placeholder="Boeing"
                  />
                </div>
                <div className="form-group">
                  <label>Вместимость</label>
                  <input
                    type="number"
                    value={editForm.capacity || ''}
                    onChange={(e) => setEditForm({ ...editForm, capacity: e.target.value ? parseInt(e.target.value) : undefined })}
                    placeholder="189"
                    min="1"
                    max="1000"
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Описание</label>
                <textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  placeholder="Описание типа самолета"
                  rows={3}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={closeEditModal} className="cancel-btn">
                Отмена
              </button>
              <button onClick={handleUpdateAircraftType} className="save-btn">
                Сохранить
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AircraftTypeManagement; 